import { useCallback, useEffect, useState } from "react";
import { get, post } from "../api";
import { useI18n } from "../i18n";

interface SourceItem {
  id: string;
  kind: string;
  enabled: boolean;
  public_site_key?: string | null;
  agent_key_hint?: string | null;
  label?: string | null;
  created_at?: string | null;
}

interface IntegrationGuideProps {
  siteId: string;
  siteName: string;
  workspaceId: string;
}

type FrameworkType = "html" | "nextjs" | "react" | "vue" | "nginx_proxy" | "nginx_log" | "custom_event";

export function IntegrationGuide({ siteId, siteName, workspaceId: _workspaceId }: IntegrationGuideProps) {
  const { t, locale } = useI18n();
  const [activeTab, setActiveTab] = useState<"human" | "ai">("human");
  const [framework, setFramework] = useState<FrameworkType>("html");
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [creatingAgentKey, setCreatingAgentKey] = useState(false);
  const [newAgentKey, setNewAgentKey] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<{ type: "ok" | "err" | "info"; text: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Determine default base host
  const defaultHost = typeof window !== "undefined"
    ? (window.location.port === "5173" ? "http://127.0.0.1:8000" : window.location.origin)
    : "https://widget.groweb.dev";
  const [hostBase, setHostBase] = useState(defaultHost);

  const loadSources = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await get<{ items: SourceItem[] }>(`/api/v1/sites/${siteId}/sources`);
      setSources(res.items || []);
    } catch {
      // Endpoint might be freshly created or error out; ignore quietly
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const browserSource = sources.find((s) => s.kind === "browser");
  const publicSiteKey = browserSource?.public_site_key || "pub_xxxxxxxxxxxxxxxx";
  const hasRealKey = Boolean(browserSource?.public_site_key);

  const originSource = sources.find((s) => s.kind === "origin");
  const hasOriginSource = Boolean(originSource);
  const originKeyHint = originSource?.agent_key_hint || null;

  const sdkUrl = `${hostBase}/tracker.js`;
  const endpointUrl = `${hostBase}/collect/v1/browser`;

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2200);
    } catch {
      setStatusMsg({ type: "err", text: locale === "zh" ? "复制失败，请手动选择文字复制" : "Copy failed, please select text manually." });
    }
  };

  const handleGenerateKey = async () => {
    setCreatingKey(true);
    setStatusMsg(null);
    try {
      const res = await post<{ public_site_key: string }>(`/api/v1/sites/${siteId}/sources`, {
        kind: "browser",
      });
      setStatusMsg({
        type: "ok",
        text: locale === "zh"
          ? `浏览器采集密钥已成功生成: ${res.public_site_key}`
          : `Browser tracking key successfully generated: ${res.public_site_key}`,
      });
      await loadSources();
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === "site_not_verified") {
        setStatusMsg({
          type: "info",
          text: locale === "zh"
            ? "站点域名尚未验证。请先返回工作空间页面，通过 DNS TXT 记录完成域名验证后再生成；您也可先使用临时占位密钥调试前端代码。"
            : "Domain not verified yet. Please complete DNS TXT verification in your workspace first, or use the placeholder key for local testing.",
        });
      } else {
        setStatusMsg({ type: "err", text: err.message || (locale === "zh" ? "生成密钥失败" : "Failed to generate key") });
      }
    } finally {
      setCreatingKey(false);
    }
  };

  const handleGenerateAgentKey = async () => {
    setCreatingAgentKey(true);
    setStatusMsg(null);
    try {
      if (hasOriginSource && originSource?.id) {
        const res = await post<{ agent_key: string; agent_key_hint: string; note?: string }>(
          `/api/v1/sources/${originSource.id}/rotate-key`,
          { grace_hours: 24 }
        );
        setNewAgentKey(res.agent_key);
        setStatusMsg({
          type: "ok",
          text: locale === "zh"
            ? `服务端 Agent Key 已成功轮换！旧密钥保持 24 小时过渡期。新密钥仅此一次展示，请立即复制保存。`
            : `Origin Agent Key rotated! New key shown once, please save.`,
        });
      } else {
        const res = await post<{ agent_key: string; agent_key_hint: string }>(
          `/api/v1/sites/${siteId}/sources`,
          { kind: "origin", label: "flowmic-nginx" }
        );
        setNewAgentKey(res.agent_key);
        setStatusMsg({
          type: "ok",
          text: locale === "zh"
            ? `服务端 Agent Key 已成功签发！注意：密钥仅此一次展示，请立即复制并妥善保存。`
            : `Origin Agent Key generated! Note: It is shown ONLY ONCE, please copy and save.`,
        });
      }
      await loadSources();
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === "site_not_verified") {
        setStatusMsg({
          type: "info",
          text: locale === "zh"
            ? "站点域名尚未通过 DNS TXT 验证，暂时无法签发有效服务端密钥。请先在工作空间完成域名验证。"
            : "Domain not verified yet. Please complete DNS TXT verification in your workspace first.",
        });
      } else {
        setStatusMsg({
          type: "err",
          text: err.message || (locale === "zh" ? "签发 Agent Key 失败" : "Failed to generate Agent Key"),
        });
      }
    } finally {
      setCreatingAgentKey(false);
    }
  };

  // HTML snippet
  const htmlSnippet = `<!-- Groweb Analytics SDK (Place before </head>) -->
<script defer
  src="${sdkUrl}"
  data-site="${publicSiteKey}"
  data-endpoint="${endpointUrl}"></script>`;

  // Next.js App Router snippet
  const nextAppSnippet = `// app/layout.tsx (Next.js 13/14/15 App Router)
import Script from "next/script";

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <Script
          strategy="afterInteractive"
          src="${sdkUrl}"
          data-site="${publicSiteKey}"
          data-endpoint="${endpointUrl}"
        />
      </head>
      <body>{children}</body>
    </html>
  );
}`;

  // React Vite snippet
  const reactViteSnippet = `<!-- index.html (Vite / CRA React App) -->
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>My App</title>
    <!-- Groweb Analytics SDK: Automatically listens to SPA pushState/popstate -->
    <script defer
      src="${sdkUrl}"
      data-site="${publicSiteKey}"
      data-endpoint="${endpointUrl}"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>`;

  // Vue / Nuxt snippet
  const vueNuxtSnippet = `// nuxt.config.ts (Nuxt 3)
export default defineNuxtConfig({
  app: {
    head: {
      script: [
        {
          defer: true,
          src: "${sdkUrl}",
          "data-site": "${publicSiteKey}",
          "data-endpoint": "${endpointUrl}",
        },
      ],
    },
  },
});`;

  // Custom Event Snippet
  const customEventSnippet = `// Custom Event Tracking (e.g. signup, purchase, CTA click)
if (typeof window !== "undefined" && (window as any).lta?.event) {
  (window as any).lta.event("signup_submit", {
    plan: "pro",
    source: "hero_cta"
  });
}`;

  // TypeScript definition snippet
  const tsDefSnippet = `// types/lta.d.ts
interface LtaGlobal {
  event: (name: string, attrs?: Record<string, string | number | boolean>) => void;
  setConsent?: (granted: boolean) => void;
  disable?: () => void;
}

declare global {
  interface Window {
    lta?: LtaGlobal;
  }
}

export {};`;

  // Nginx First-Party Proxy Snippet
  const nginxProxySnippet = `# ==============================================================================
# 模式 A：Nginx 第一方同源反向代理（前端 JS SDK 接入的最佳实践）
# 适用场景：前端加载 tracker.js 时完全同源，100% 避开浏览器广告插件屏蔽，隔离业务 Cookie。
# ==============================================================================

# 在您已有站点的 Nginx server {} 块中加入：
location /_gt/ {
    proxy_pass ${hostBase}/;
    proxy_set_header Cookie "";
    proxy_set_header Authorization "";
    proxy_set_header X-Real-IP $remote_addr;
    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
    proxy_set_header X-Forwarded-Proto $scheme;
    proxy_http_version 1.1;
    proxy_set_header Connection "";
}

# 此时网页 HTML 中直接使用同源相对路径，无需跨域：
# <script defer
#         src="/_gt/tracker.js"
#         data-site="${publicSiteKey}"
#         data-endpoint="/_gt/collect/v1/browser"></script>`;

  // Nginx JSON Log Snippet
  const nginxLogSnippet = `# ==============================================================================
# 模式 B：纯 Nginx 服务端日志接入（无需在网页插入任何 JS 代码！）
# 适用场景：全量统计 HTTP 请求、API 访问、爬虫抓取与后端耗时，对业务代码 0 侵入。
# ==============================================================================

# 步骤 1：在 /etc/nginx/nginx.conf 的 http {} 块中声明 JSON 日志格式
log_format lta_json escape=json '{'
  '"request_id":"$request_id",'
  '"ended_at_unix":"$msec",'
  '"host":"$host",'
  '"path":"$request_uri",'
  '"method":"$request_method",'
  '"status":$status,'
  '"bytes_sent":$bytes_sent,'
  '"request_time_s":"$request_time",'
  '"referrer_host":"$http_referer",'
  '"user_agent":"$http_user_agent"'
'}';

# 在需要监控的虚拟主机 server {} 块中开启日志输出（支持异步缓冲 flush=1s）
access_log /var/log/nginx/lta_access.jsonl lta_json buffer=32k flush=1s;

# ------------------------------------------------------------------------------
# 步骤 2：配置日志采集器（以 Vector 为例，将日志实时推送到 Groweb Analytics）
# 编辑 /etc/vector/vector.toml：
# ------------------------------------------------------------------------------
# [sources.nginx_logs]
# type = "file"
# include = ["/var/log/nginx/lta_access.jsonl"]
# read_from = "end"
#
# [transforms.parse_json]
# type = "remap"
# inputs = ["nginx_logs"]
# source = '''
#   . = parse_json!(.message)
# '''
#
# [sinks.groweb_origin]
# type = "http"
# inputs = ["parse_json"]
# uri = "${endpointUrl.replace('/browser', '/origin')}"
# method = "post"
# encoding.codec = "json"
# batch.max_events = 100
# batch.timeout_secs = 1
# request.headers."Authorization" = "Bearer YOUR_AGENT_KEY"
# request.headers."Content-Type" = "application/json"`;

  const originEndpointUrl = endpointUrl.replace('/browser', '/origin');

  // AI Prompt content (Bilingual based on current locale)
  const aiPrompt = locale === "zh"
    ? `你是一名全栈工程师。请为当前代码库接入 Web 流量与体验分析 SDK (Groweb Analytics)。

### 1. 站点配置与凭据
- 站点名称：${siteName}
- 站点 ID：${siteId}
- 公开站点密钥 (Public Site Key)：${publicSiteKey}
- 前端上报端点 (Browser Endpoint)：${endpointUrl}
- SDK 脚本地址 (Script URL)：${sdkUrl}
- 服务端日志上报端点 (Origin Endpoint)：${originEndpointUrl}

### 2. 方案选择与集成规范

#### 模式 A：前端直接引入（标准 Web 应用推荐）
1. **自动识别当前前端技术栈**：
   - Next.js (App Router)：在 app/layout.tsx 的 <head> 中使用 next/script（strategy="afterInteractive"）或原生 <script defer ...>。
   - Vite / Webpack React SPA：在 index.html 的 <head> 中引入。
   - Nuxt 3 / Vue 3：在 nuxt.config.ts 的 app.head.script 中配置。
   - 通用静态 HTML：在公共模板的 <head> 中引入。

2. **HTML Script 引入标签**：
\`\`\`html
<script defer
  src="${sdkUrl}"
  data-site="${publicSiteKey}"
  data-endpoint="${endpointUrl}"></script>
\`\`\`
   - 必须使用 defer 异步属性，保障 Core Web Vitals 评分。
   - SDK 原生支持 SPA 路由监听与 Web Vitals (LCP, INP, CLS) 采集。

3. **创建前端类型安全封装**：
   在 src/lib/analytics.ts 封装 trackEvent(name: string, attrs?: Record<string, any>)，包含 typeof window !== "undefined" 保护与防广告拦截安全降级。

---

#### 模式 B：Nginx 第一方同源反向代理（高可靠防广告插件屏蔽）
若当前项目运行在 Nginx 之后，或项目中包含 nginx.conf / default.conf，建议配置反向代理实现 100% 同源交付，绕过 AdBlock 等插件拦截并剥离业务 Cookie：
1. **在 Nginx 站点的 server {} 块中添加反代 location**：
\`\`\`nginx
location /_gt/ {
    proxy_pass ${hostBase}/;
    proxy_set_header Cookie "";
    proxy_set_header Authorization "";
    proxy_set_header X-Real-IP $remote_addr;
    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
    proxy_set_header X-Forwarded-Proto $scheme;
    proxy_http_version 1.1;
    proxy_set_header Connection "";
}
\`\`\`
2. **将前端 HTML / 框架模板中的脚本路径改为同源相对路径**：
\`\`\`html
<script defer
  src="/_gt/tracker.js"
  data-site="${publicSiteKey}"
  data-endpoint="/_gt/collect/v1/browser"></script>
\`\`\`

---

#### 模式 C：Nginx 服务端日志旁路直采（零前端改动 / 纯运维模式）
若当前代码库无前端页面（纯 API 网关），或客户要求不改动任何业务前端代码：
1. **在 /etc/nginx/nginx.conf 的 http {} 块中声明 JSON 日志格式**：
\`\`\`nginx
log_format lta_json escape=json '{'
  '"request_id":"$request_id",'
  '"ended_at_unix":"$msec",'
  '"host":"$host",'
  '"path":"$request_uri",'
  '"method":"$request_method",'
  '"status":$status,'
  '"bytes_sent":$bytes_sent,'
  '"request_time_s":"$request_time",'
  '"referrer_host":"$http_referer",'
  '"user_agent":"$http_user_agent"'
'}';
\`\`\`
2. **在对应虚拟主机 server {} 块启用日志缓冲输出**：
\`\`\`nginx
access_log /var/log/nginx/lta_access.jsonl lta_json buffer=32k flush=1s;
\`\`\`
3. **配合日志采集器（如 Vector / Fluent-Bit）将日志行 POST 至**：
   \`${originEndpointUrl}\`，并在请求头中携带 \`Authorization: Bearer <AGENT_KEY>\`。`
    : `You are a Senior Full-Stack Engineer. Please integrate Groweb Analytics into this codebase.

### 1. Credentials & Configuration
- Site Name: ${siteName}
- Site ID: ${siteId}
- Public Site Key: ${publicSiteKey}
- Browser Ingest Endpoint: ${endpointUrl}
- Script URL: ${sdkUrl}
- Origin Log Ingest Endpoint: ${originEndpointUrl}

### 2. Integration Modes & Requirements

#### Mode A: Client-Side Direct Integration (Standard)
1. **Identify Framework**:
   - Next.js (App Router): Place in app/layout.tsx inside <head> using <Script strategy="afterInteractive" ... />.
   - Vite / React SPA: Place inside <head> of index.html.
   - Nuxt 3 / Vue: Configure in nuxt.config.ts under app.head.script.
   - Static HTML: Place before </head>.

2. **Snippet**:
\`\`\`html
<script defer
  src="${sdkUrl}"
  data-site="${publicSiteKey}"
  data-endpoint="${endpointUrl}"></script>
\`\`\`
   - Must include the "defer" attribute to guarantee non-blocking asynchronous load.
   - SPA route changes and Core Web Vitals (LCP, INP, CLS) are automatically captured.

3. **Typed Helper**:
   Create a safe helper in src/lib/analytics.ts with a \`trackEvent(name: string, attrs?: Record<string, string | number | boolean>)\` function, SSR guards (\`typeof window !== "undefined"\`), and silent fallback if blocked.

---

#### Mode B: Nginx First-Party Reverse Proxy (Recommended for AdBlock Resistance)
If this codebase contains Nginx configurations (e.g. nginx.conf, Dockerfile) or is deployed behind Nginx, proxy the SDK and endpoints via your own domain to avoid ad-blocker false positives and strip visitor cookies:
1. **Add proxy location block inside Nginx server {}**:
\`\`\`nginx
location /_gt/ {
    proxy_pass ${hostBase}/;
    proxy_set_header Cookie "";
    proxy_set_header Authorization "";
    proxy_set_header X-Real-IP $remote_addr;
    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
    proxy_set_header X-Forwarded-Proto $scheme;
    proxy_http_version 1.1;
    proxy_set_header Connection "";
}
\`\`\`
2. **Update frontend script tag to same-origin relative path**:
\`\`\`html
<script defer
  src="/_gt/tracker.js"
  data-site="${publicSiteKey}"
  data-endpoint="/_gt/collect/v1/browser"></script>
\`\`\`

---

#### Mode C: Nginx Server Access Log Pipeline (Zero Frontend Code)
If you need pure backend/edge access analytics without modifying any client-side source code:
1. **Define JSON log format in /etc/nginx/nginx.conf (http {} block)**:
\`\`\`nginx
log_format lta_json escape=json '{'
  '"request_id":"$request_id",'
  '"ended_at_unix":"$msec",'
  '"host":"$host",'
  '"path":"$request_uri",'
  '"method":"$request_method",'
  '"status":$status,'
  '"bytes_sent":$bytes_sent,'
  '"request_time_s":"$request_time",'
  '"referrer_host":"$http_referer",'
  '"user_agent":"$http_user_agent"'
'}';
\`\`\`
2. **Enable access log in server {}**:
\`\`\`nginx
access_log /var/log/nginx/lta_access.jsonl lta_json buffer=32k flush=1s;
\`\`\`
3. **Ship logs via Vector / Fluent-Bit HTTP sink to**:
   \`${originEndpointUrl}\` with header \`Authorization: Bearer <AGENT_KEY>\`.`;

  return (
    <div className="card" style={{ marginTop: 24, border: "1px solid #cbd5e1", background: "#ffffff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
            <span>🌐 {t.guide.title}</span>
            <span style={{ fontSize: 12, fontWeight: "normal", color: "#64748b" }}>
              ({siteName})
            </span>
          </h3>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
            {t.guide.subtitle}
          </p>
        </div>

        {/* Tab switch buttons */}
        <div style={{ display: "flex", background: "#f1f5f9", padding: 3, borderRadius: 8, gap: 4 }}>
          <button
            type="button"
            onClick={() => setActiveTab("human")}
            style={{
              border: "none",
              background: activeTab === "human" ? "#ffffff" : "transparent",
              color: activeTab === "human" ? "#2563eb" : "#64748b",
              fontWeight: activeTab === "human" ? "600" : "normal",
              boxShadow: activeTab === "human" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              padding: "6px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {t.guide.tabHuman}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("ai")}
            style={{
              border: "none",
              background: activeTab === "ai" ? "#2563eb" : "transparent",
              color: activeTab === "ai" ? "#ffffff" : "#64748b",
              fontWeight: activeTab === "ai" ? "600" : "normal",
              boxShadow: activeTab === "ai" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              padding: "6px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>{t.guide.tabAi}</span>
          </button>
        </div>
      </div>

      {/* Key Status & Configuration Bar (Sources & Keys Management) */}
      <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, borderBottom: "1px solid #e2e8f0", paddingBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: "600", color: "#1e293b" }}>
            🔑 {locale === "zh" ? "数据采集来源与凭据管理 (Data Sources & Credentials)" : "Data Sources & Credentials"}
          </span>
          <button
            type="button"
            className="ghost"
            style={{ fontSize: 12, padding: "3px 8px" }}
            disabled={loading}
            onClick={loadSources}
          >
            {loading ? "…" : t.guide.refreshKey}
          </button>
        </div>

        {/* Row 1: Browser Source */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: "600", color: "#475569", minWidth: 160 }}>
              🌐 {locale === "zh" ? "浏览器前端密钥 (Public Key):" : "Browser Public Key:"}
            </span>
            <code style={{ background: "#e2e8f0", padding: "2px 8px", borderRadius: 4, fontWeight: "bold", fontSize: 13 }}>
              {publicSiteKey}
            </code>
            {hasRealKey ? (
              <span className="badge ok" style={{ fontSize: 11 }}>{t.guide.keyStatusGenerated}</span>
            ) : (
              <span className="badge warn" style={{ fontSize: 11 }}>{t.guide.keyStatusPlaceholder}</span>
            )}
          </div>

          {!hasRealKey && (
            <button
              type="button"
              className="primary"
              style={{ fontSize: 12, padding: "3px 10px" }}
              disabled={creatingKey}
              onClick={handleGenerateKey}
            >
              {creatingKey ? t.guide.generatingKey : t.guide.generateKey}
            </button>
          )}
        </div>

        {/* Row 2: Origin Agent Source */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, paddingTop: 8, borderTop: "1px dashed #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: "600", color: "#475569", minWidth: 160 }}>
              🖥️ {locale === "zh" ? "服务端 Agent 密钥 (Origin Key):" : "Origin Agent Key:"}
            </span>
            <code style={{ background: "#e2e8f0", padding: "2px 8px", borderRadius: 4, fontWeight: "bold", fontSize: 13 }}>
              {originKeyHint ? originKeyHint : (locale === "zh" ? "尚未签发" : "Not configured")}
            </code>
            {hasOriginSource ? (
              <span className="badge ok" style={{ fontSize: 11 }}>
                {locale === "zh" ? "已激活 (服务端日志通道就绪)" : "Active (Origin Ready)"}
              </span>
            ) : (
              <span className="badge warn" style={{ fontSize: 11 }}>
                {locale === "zh" ? "未配置" : "Unconfigured"}
              </span>
            )}
          </div>

          <button
            type="button"
            className={hasOriginSource ? "ghost" : "primary"}
            style={{ fontSize: 12, padding: "3px 10px" }}
            disabled={creatingAgentKey}
            onClick={handleGenerateAgentKey}
          >
            {creatingAgentKey
              ? (locale === "zh" ? "处理中…" : "Processing…")
              : hasOriginSource
              ? (locale === "zh" ? "🔄 轮换密钥 (Rotate Key)" : "🔄 Rotate Key")
              : (locale === "zh" ? "⚡ 签发服务端 Agent Key (flowmic-nginx)" : "⚡ Issue Agent Key")}
          </button>
        </div>

        {/* One-time Agent Key Reveal Banner */}
        {newAgentKey && (
          <div style={{
            marginTop: 12,
            padding: "10px 14px",
            background: "#f0fdf4",
            border: "2px solid #22c55e",
            borderRadius: 6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <span style={{ fontWeight: "bold", color: "#15803d", fontSize: 13 }}>
                  🔑 {locale === "zh" ? "新签发的 Agent Key（仅此一次展示，请立即复制）：" : "Newly Issued Agent Key (Shown ONCE, please copy):"}
                </span>
                <div style={{ marginTop: 4 }}>
                  <code style={{ background: "#ffffff", padding: "4px 10px", borderRadius: 4, fontSize: 13, border: "1px solid #86efac", color: "#166534", wordBreak: "break-all" }}>
                    {newAgentKey}
                  </code>
                </div>
              </div>
              <button
                type="button"
                className="primary"
                style={{ fontSize: 12, padding: "6px 14px", background: "#16a34a" }}
                onClick={() => copyToClipboard(newAgentKey, "agent-key-secret")}
              >
                {copiedId === "agent-key-secret"
                  ? (locale === "zh" ? "✅ 已复制到剪贴板" : "✅ Copied!")
                  : (locale === "zh" ? "📋 复制 Agent Key" : "📋 Copy Agent Key")}
              </button>
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#166534" }}>
              {locale === "zh"
                ? "系统数据库遵循高安全审计标准，仅存储该密钥的 SHA-256 哈希散列，刷新页面后将无法再次查看明文！请尽快配置到 Vector 或源站 Nginx 日志转发器中。"
                : "Security note: The database stores only the SHA-256 hash. This plaintext will not be shown again after page refresh. Store it in your Vector/Nginx config immediately."}
            </p>
          </div>
        )}

        {/* API / CLI Instructions Accordion */}
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #e2e8f0" }}>
          <details style={{ fontSize: 12, color: "#475569" }}>
            <summary style={{ cursor: "pointer", fontWeight: "600", color: "#2563eb" }}>
              📖 {locale === "zh" ? "查看通过 REST API / CLI 自动化签发与轮换 AGENT_KEY 的两条操作路径说明" : "View REST API & CLI instructions for issuing & rotating AGENT_KEY"}
            </summary>
            <div style={{ marginTop: 8, padding: "10px 12px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6 }}>
              <p style={{ margin: "0 0 6px", fontWeight: "600", color: "#1e293b" }}>
                {locale === "zh" ? "路径一：Web 控制台界面操作（推荐人工配置）" : "Path 1: Web Console UI (Manual)"}
              </p>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18, lineHeight: 1.6 }}>
                <li>{locale === "zh" ? "首次签发：点击上方的「⚡ 签发服务端 Agent Key」按钮，系统生成后将在绿色卡片中一次性展示密钥明文。" : "First issue: Click '⚡ Issue Agent Key' above. Plaintext is shown once."}</li>
                <li>{locale === "zh" ? "定期轮换：点击上方的「🔄 轮换密钥 (Rotate Key)」按钮，旧密钥保持 24 小时过渡缓冲，新密钥一次性展示。" : "Rotation: Click '🔄 Rotate Key' above. Grace period of 24h applied."}</li>
              </ul>

              <p style={{ margin: "0 0 6px", fontWeight: "600", color: "#1e293b" }}>
                {locale === "zh" ? "路径二：REST API / CLI 自动化操作（推荐 CI/CD 与自动化脚本）" : "Path 2: REST API & CLI (Automated)"}
              </p>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontWeight: "bold" }}>1. {locale === "zh" ? "初始签发 Origin Agent Key：" : "Initial Agent Key Generation:"}</span>
                <pre style={{ background: "#0f172a", color: "#f8fafc", padding: "8px 12px", borderRadius: 4, overflowX: "auto", margin: "4px 0" }}>
                  {`curl -X POST ${hostBase}/api/v1/sites/${siteId}/sources \\
  -H "Content-Type: application/json" \\
  -H "Origin: ${hostBase}" \\
  -H "Cookie: lta_session=<YOUR_SESSION_COOKIE>" \\
  -d '{"kind": "origin", "label": "flowmic-nginx"}'`}
                </pre>
                <span className="muted" style={{ fontSize: 11 }}>
                  {locale === "zh" ? "响应 (201 Created) 将返回包含 agent_key 的 JSON 对象，明文仅展示一次。" : "Response returns agent_key plaintext only once."}
                </span>
              </div>

              {hasOriginSource && originSource?.id && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: "bold" }}>2. {locale === "zh" ? "轮换已有 Agent Key (双 Key 平滑过渡)：" : "Rotate Existing Agent Key (Dual-Key Overlap):"}</span>
                  <pre style={{ background: "#0f172a", color: "#f8fafc", padding: "8px 12px", borderRadius: 4, overflowX: "auto", margin: "4px 0" }}>
                    {`curl -X POST ${hostBase}/api/v1/sources/${originSource.id}/rotate-key \\
  -H "Content-Type: application/json" \\
  -H "Origin: ${hostBase}" \\
  -H "Cookie: lta_session=<YOUR_SESSION_COOKIE>" \\
  -d '{"grace_hours": 24}'`}
                  </pre>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {locale === "zh" ? "响应 (200 OK) 将返回包含新 agent_key 的 JSON 对象，旧密钥在 grace_hours 内仍然有效。" : "Returns new agent_key, old key valid during grace period."}
                  </span>
                </div>
              )}

              <div>
                <span style={{ fontWeight: "bold" }}>3. {locale === "zh" ? "查询站点当前数据来源列表：" : "List Existing Sources:"}</span>
                <pre style={{ background: "#0f172a", color: "#f8fafc", padding: "8px 12px", borderRadius: 4, overflowX: "auto", margin: "4px 0" }}>
                  {`curl -X GET ${hostBase}/api/v1/sites/${siteId}/sources \\
  -H "Cookie: lta_session=<YOUR_SESSION_COOKIE>"`}
                </pre>
                <span className="muted" style={{ fontSize: 11 }}>
                  {locale === "zh" ? "返回当前站点下所有已启用的来源信息及脱敏密钥提示 (agent_key_hint)。" : "Returns all active sources with masked key hints."}
                </span>
              </div>
            </div>
          </details>
        </div>

        {/* Optional Host Override */}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed #e2e8f0", display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#64748b" }}>
          <span>Base Host:</span>
          <input
            style={{ width: 280, padding: "3px 8px", fontSize: 12, borderRadius: 4, border: "1px solid #cbd5e1" }}
            value={hostBase}
            onChange={(e) => setHostBase(e.target.value)}
            placeholder="https://widget.groweb.dev"
          />
        </div>

        {statusMsg && (
          <div style={{
            marginTop: 8,
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 12,
            background: statusMsg.type === "ok" ? "#f0fdf4" : statusMsg.type === "err" ? "#fef2f2" : "#fefce8",
            color: statusMsg.type === "ok" ? "#166534" : statusMsg.type === "err" ? "#991b1b" : "#854d0e",
            border: `1px solid ${statusMsg.type === "ok" ? "#bbf7d0" : statusMsg.type === "err" ? "#fecaca" : "#fef08a"}`,
          }}>
            {statusMsg.text}
          </div>
        )}
      </div>

      {/* TAB 1: HUMAN GUIDE */}
      {activeTab === "human" && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: "#1e293b" }}>
                {t.guide.step1Title}
              </h4>
              <button
                type="button"
                className="ghost"
                style={{ fontSize: 12, padding: "2px 8px", color: copiedId === "html" ? "#16a34a" : "#2563eb" }}
                onClick={() => copyToClipboard(htmlSnippet, "html")}
              >
                {copiedId === "html" ? t.guide.copiedCode : t.guide.copyCode}
              </button>
            </div>
            <pre style={{
              background: "#0f172a",
              color: "#f8fafc",
              padding: "12px 14px",
              borderRadius: 8,
              fontSize: 13,
              overflowX: "auto",
              lineHeight: 1.5,
              margin: 0,
            }}>
              {htmlSnippet}
            </pre>
          </div>

          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#1e293b" }}>
              {t.guide.step2Title}
            </h4>
            <div style={{ marginBottom: 10, fontSize: 12, color: "#475569", background: "#f8fafc", padding: "8px 12px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
              {locale === "zh"
                ? "💡 支持两种核心集成模式：① 前端网页埋点（统计真实访客 UV/PV、停留时长与 Web Vitals）；② Nginx 服务端旁路日志（无需任何网页改动，统计全站真实 HTTP 请求、API 与爬虫）。"
                : "💡 Two core integration modes supported: ① Frontend Web SDK (UV/PV, dwell time, and Core Web Vitals); ② Nginx Server-Side Access Log (0 frontend code changes, captures all HTTP requests, APIs & crawlers)."}
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              {[
                { id: "html", label: "Vanilla HTML" },
                { id: "nextjs", label: "Next.js" },
                { id: "react", label: "React (Vite / CRA)" },
                { id: "vue", label: "Vue 3 / Nuxt 3" },
                { id: "nginx_proxy", label: locale === "zh" ? "🛡️ Nginx 同源反代 (防拦截)" : "🛡️ Nginx Reverse Proxy" },
                { id: "nginx_log", label: locale === "zh" ? "🖥️ Nginx 日志旁路采集 (免网页改动)" : "🖥️ Nginx Server Log (0 Code)" },
                { id: "custom_event", label: "Custom Events & TS" },
              ].map((fw) => (
                <button
                  key={fw.id}
                  type="button"
                  onClick={() => setFramework(fw.id as any)}
                  style={{
                    fontSize: 12,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: framework === fw.id ? "1px solid #2563eb" : "1px solid #e2e8f0",
                    background: framework === fw.id ? "#eff6ff" : "#ffffff",
                    color: framework === fw.id ? "#2563eb" : "#475569",
                    fontWeight: framework === fw.id ? "600" : "normal",
                  }}
                >
                  {fw.label}
                </button>
              ))}
            </div>

            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="ghost"
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  fontSize: 11,
                  padding: "2px 8px",
                  background: "rgba(255,255,255,0.15)",
                  color: copiedId === framework ? "#4ade80" : "#ffffff",
                  borderRadius: 4,
                  zIndex: 2,
                }}
                onClick={() => {
                  const textMap: Record<string, string> = {
                    html: htmlSnippet,
                    nextjs: nextAppSnippet,
                    react: reactViteSnippet,
                    vue: vueNuxtSnippet,
                    nginx_proxy: nginxProxySnippet,
                    nginx_log: nginxLogSnippet,
                    custom_event: customEventSnippet + "\n\n" + tsDefSnippet,
                  };
                  copyToClipboard(textMap[framework], framework);
                }}
              >
                {copiedId === framework ? t.guide.copiedCode : t.guide.copyCode}
              </button>

              <pre style={{
                background: "#0f172a",
                color: "#f8fafc",
                padding: "12px 14px",
                borderRadius: 8,
                fontSize: 13,
                overflowX: "auto",
                lineHeight: 1.5,
                margin: 0,
              }}>
                {framework === "html" && htmlSnippet}
                {framework === "nextjs" && nextAppSnippet}
                {framework === "react" && reactViteSnippet}
                {framework === "vue" && vueNuxtSnippet}
                {framework === "nginx_proxy" && nginxProxySnippet}
                {framework === "nginx_log" && nginxLogSnippet}
                {framework === "custom_event" && `${customEventSnippet}\n\n${tsDefSnippet}`}
              </pre>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <b style={{ fontSize: 13, color: "#166534" }}>{t.guide.feature1Title}</b>
              <p style={{ fontSize: 12, color: "#475569", margin: "4px 0 0" }}>
                {t.guide.feature1Desc}
              </p>
            </div>

            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <b style={{ fontSize: 13, color: "#1d4ed8" }}>{t.guide.feature2Title}</b>
              <p style={{ fontSize: 12, color: "#475569", margin: "4px 0 0" }}>
                {t.guide.feature2Desc}
              </p>
            </div>

            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <b style={{ fontSize: 13, color: "#1e293b" }}>{t.guide.feature3Title}</b>
              <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0" }}>
                {t.guide.feature3Desc}
              </p>
            </div>

            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <b style={{ fontSize: 13, color: "#1e293b" }}>{t.guide.feature4Title}</b>
              <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0" }}>
                {t.guide.feature4Desc}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: AI CODING PROMPT */}
      {activeTab === "ai" && (
        <div>
          <div style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
            color: "#ffffff",
            padding: "16px 20px",
            borderRadius: 8,
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{t.guide.aiBannerTitle}</span>
              </div>
              <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.9 }}>
                {t.guide.aiBannerDesc}
              </p>
            </div>

            <button
              type="button"
              onClick={() => copyToClipboard(aiPrompt, "ai-full")}
              style={{
                background: "#ffffff",
                color: "#1d4ed8",
                border: "none",
                fontWeight: "bold",
                padding: "10px 18px",
                borderRadius: 8,
                fontSize: 14,
                cursor: "pointer",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {copiedId === "ai-full" ? `✅ ${t.guide.copiedCode}` : `📋 ${t.guide.copyAiPromptBtn}`}
            </button>
          </div>

          <pre style={{
            background: "#0f172a",
            color: "#e2e8f0",
            padding: "14px 16px",
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.6,
            margin: 0,
            maxHeight: 380,
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {aiPrompt}
          </pre>
        </div>
      )}
    </div>
  );
}
